import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { MapPin, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { MetricsColumnFilter, MetricColumn } from '../shared/MetricsColumnFilter';
import { PitchSelector } from '../pitch/PitchSelector';
import { byPitchOptions, usePitchSelection } from '../pitch/pitchSelection';
import type { VideoPitch } from '@/types/pitch';
import { CountryFlag } from '@/lib/country-flags';
import '../metrics-table.css';

interface LocationMetrics {
  location: string;
  type: 'country' | 'state' | 'city';
  views: number;
  uniqueViews: number;
  plays: number;
  uniquePlays: number;
  playRate: number;
  buttonClicks: number;
  engagement: number;
  /** Retenção (%) no pitch escolhido no seletor. null = sem pitch ou sem dado. */
  pitchRetention: number | null;
  conversions: number;
  revenue: number;
  flag?: string;
}

const defaultColumns: MetricColumn[] = [
  { id: 'visualizations', label: 'Visualizações', visible: true },
  { id: 'uniqueViews', label: 'Vis. Únicas', visible: true },
  { id: 'plays', label: 'Plays', visible: true },
  { id: 'uniquePlays', label: 'Plays Únicos', visible: true },
  { id: 'playRate', label: 'Play Rate', visible: true },
  { id: 'buttonClicks', label: 'C. no Botão', visible: false },
  { id: 'engagement', label: 'Engajamento', visible: true },
  { id: 'pitchRetention', label: 'Pitch', visible: false },
  { id: 'conversions', label: 'Conversões', visible: false },
  { id: 'revenue', label: 'Receita', visible: false },
];

interface RegionalMetricsTableProps {
  type?: 'countries' | 'states' | 'cities';
  data?: any[];
  /** `video.pitches` do consolidado — opções do seletor e índice em `pitchesRetentionPct`. */
  pitches?: VideoPitch[];
}

type SortField = keyof LocationMetrics | null;
type SortDirection = 'asc' | 'desc';

export function RegionalMetricsTable({ type = 'states', data: apiData = [], pitches = [] }: RegionalMetricsTableProps) {
  useLanguage();
  // Seletor de pitch (só aparece com 2+ pitches): qual pitch preenche a coluna "Pitch".
  const pitchOptions = byPitchOptions(pitches);
  const [pitchId, setPitchId] = usePitchSelection(pitchOptions);
  const pitchIndex = pitches.findIndex((p) => p.id === pitchId);

  // Mapear dados da API para o formato da tabela
  const locationMetrics: LocationMetrics[] = apiData.map(item => {
    const isCountry = type === 'countries';
    const isCity = type === 'cities';
    const locationName = isCountry
      ? (item.pais === 'BR' ? 'Brasil' : item.pais === '0' ? 'Desconhecido' : item.pais)
      : isCity
        ? (item.cidade === '0' || !item.cidade ? 'Desconhecido' : item.cidade)
        : (item.estado === '0' ? 'Desconhecido' : item.estado);

    return {
      location: locationName,
      type: isCountry ? 'country' as const : isCity ? 'city' as const : 'state' as const,
      views: item.views || 0,
      uniqueViews: item.uniqueViews || 0,
      plays: item.plays || 0,
      uniquePlays: item.uniquePlays || 0,
      playRate: item.plays && item.views ? parseFloat(((item.plays / item.views) * 100).toFixed(2)) : 0,
      buttonClicks: 0, // TODO: adicionar quando backend enviar
      engagement: parseFloat(item.engajamento || '0'),
      pitchRetention: pitchIndex >= 0 ? item.pitchesRetentionPct?.[pitchIndex] ?? null : null,
      conversions: 0, // TODO: adicionar quando backend enviar
      revenue: 0 // TODO: adicionar quando backend enviar
    };
  });

  const [columns, setColumns] = useState<MetricColumn[]>(defaultColumns);
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
  useLanguage();
    if (sortField !== field) {
      return <ArrowUpDown className="ml-2 h-3 w-3 text-[#AEB8C9] group-hover:text-[#6E7A95] transition-colors" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="ml-2 h-3 w-3 text-[#4B7BFF]" />
    ) : (
      <ArrowDown className="ml-2 h-3 w-3 text-[#4B7BFF]" />
    );
  };

  const sortedData = [...locationMetrics].sort((a, b) => {
    if (!sortField) return 0;
    
    const aValue = a[sortField] ?? -1;
    const bValue = b[sortField] ?? -1;
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    return 0;
  });

  // Header cell component for consistency
  const HeaderCell = ({ field, label, className = '' }: { field: SortField; label: string; className?: string }) => { useLanguage(); return ((
    <TableHead 
      className={`text-right bg-white ${className}`}
      style={{ 
        borderBottom: '1px solid #EDF1FF',
        padding: '10px 12px',
        height: '48px',
        fontSize: '12px',
        fontWeight: 600,
        color: '#5A6475'
      }}
    >
      <Button
        variant="ghost"
        onClick={() => handleSort(field)}
        className="h-auto p-0 hover:bg-transparent font-semibold ml-auto flex text-[#5A6475] hover:text-[#4B7BFF] group transition-colors text-[12px]"
      >
        {label}
        <SortIcon field={field} />
      </Button>
    </TableHead>
  )); };
  
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end gap-3">
        {columns.find(c => c.id === 'pitchRetention')?.visible && (
          <PitchSelector options={pitchOptions} value={pitchId} onChange={setPitchId} />
        )}
        <MetricsColumnFilter columns={columns} onColumnsChange={setColumns} />
      </div>
      
      <Card 
        className="metrics-table-container metrics-table-premium overflow-hidden"
        style={{ marginTop: '24px' }}
      >
        <div className="metrics-table-wrapper">
          <Table>
            <TableHeader>
              <TableRow className="border-0">
                <TableHead 
                  className="font-semibold w-[160px] bg-white"
                  style={{ 
                    borderBottom: '1px solid #EDF1FF',
                    padding: '10px 12px',
                    height: '48px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#5A6475'
                  }}
                >
                  <Button
                    variant="ghost"
                    onClick={() => handleSort('location')}
                    className="h-auto p-0 hover:bg-transparent font-semibold text-[#5A6475] hover:text-[#4B7BFF] group transition-colors text-[12px]"
                  >
                    {type === 'countries' ? translateInterfaceText("País") : type === 'cities' ? translateInterfaceText("Cidade") : translateInterfaceText("Estado")}
                    <SortIcon field="location" />
                  </Button>
                </TableHead>
                {columns.find(c => c.id === 'visualizations')?.visible && (
                  <HeaderCell field="views" label={translateInterfaceText("Visualizações")} className="min-w-[100px]" />
                )}
                {columns.find(c => c.id === 'uniqueViews')?.visible && (
                  <HeaderCell field="uniqueViews" label={translateInterfaceText("Vis. Únicas")} className="min-w-[100px]" />
                )}
                {columns.find(c => c.id === 'plays')?.visible && (
                  <HeaderCell field="plays" label={translateInterfaceText("Plays")} className="min-w-[80px]" />
                )}
                {columns.find(c => c.id === 'uniquePlays')?.visible && (
                  <HeaderCell field="uniquePlays" label={translateInterfaceText("Plays Únicos")} className="min-w-[90px]" />
                )}
                {columns.find(c => c.id === 'playRate')?.visible && (
                  <HeaderCell field="playRate" label={translateInterfaceText("Play Rate")} className="min-w-[80px]" />
                )}
                {columns.find(c => c.id === 'buttonClicks')?.visible && (
                  <HeaderCell field="buttonClicks" label={translateInterfaceText("C. no Botão")} className="min-w-[90px]" />
                )}
                {columns.find(c => c.id === 'engagement')?.visible && (
                  <HeaderCell field="engagement" label={translateInterfaceText("Engajamento")} className="min-w-[90px]" />
                )}
                {columns.find(c => c.id === 'pitchRetention')?.visible && (
                  <HeaderCell field="pitchRetention" label={translateInterfaceText("Pitch")} className="min-w-[80px]" />
                )}
                {columns.find(c => c.id === 'conversions')?.visible && (
                  <HeaderCell field="conversions" label={translateInterfaceText("Conversões")} className="min-w-[90px]" />
                )}
                {columns.find(c => c.id === 'revenue')?.visible && (
                  <HeaderCell field="revenue" label={translateInterfaceText("Receita")} className="min-w-[100px]" />
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedData.map((location, index) => (
                <TableRow 
                  key={index}
                  className={`${index % 2 === 0 ? 'bg-white' : 'bg-[#FAFBFF]'} hover:bg-[#F3F6FF] transition-all duration-150 border-b border-[#EDF1FF]`}
                  style={{ height: '44px' }}
                >
                  <TableCell 
                    className="font-semibold text-[#2C3345] text-[12px]"
                    style={{ padding: '8px 12px' }}
                  >
                    <div className="flex items-center gap-2">
                      {type === 'countries' && (
                        <div className="rounded overflow-hidden shadow-sm">
                          <CountryFlag country={location.location} size={18} />
                        </div>
                      )}
                      {(type === 'states' || type === 'cities') && (
                        <MapPin className="h-3.5 w-3.5 text-[#7E8492]" />
                      )}
                      <span>{location.location}</span>
                    </div>
                  </TableCell>
                  {columns.find(c => c.id === 'visualizations')?.visible && (
                    <TableCell className="text-right font-semibold text-[#1C2434] text-[12px]" style={{ padding: '8px 12px' }}>
                      {location.views.toLocaleString(getInterfaceLanguage())}
                    </TableCell>
                  )}
                  {columns.find(c => c.id === 'uniqueViews')?.visible && (
                    <TableCell className="text-right font-semibold text-[#1C2434] text-[12px]" style={{ padding: '8px 12px' }}>
                      {location.uniqueViews.toLocaleString(getInterfaceLanguage())}
                    </TableCell>
                  )}
                  {columns.find(c => c.id === 'plays')?.visible && (
                    <TableCell className="text-right font-semibold text-[#1C2434] text-[12px]" style={{ padding: '8px 12px' }}>
                      {location.plays.toLocaleString(getInterfaceLanguage())}
                    </TableCell>
                  )}
                  {columns.find(c => c.id === 'uniquePlays')?.visible && (
                    <TableCell className="text-right font-semibold text-[#1C2434] text-[12px]" style={{ padding: '8px 12px' }}>
                      {location.uniquePlays.toLocaleString(getInterfaceLanguage())}
                    </TableCell>
                  )}
                  {columns.find(c => c.id === 'playRate')?.visible && (
                    <TableCell className="text-right font-semibold text-[#1C2434] text-[12px]" style={{ padding: '8px 12px' }}>
                      {location.playRate}%
                    </TableCell>
                  )}
                  {columns.find(c => c.id === 'buttonClicks')?.visible && (
                    <TableCell className="text-right font-semibold text-[#1C2434] text-[12px]" style={{ padding: '8px 12px' }}>
                      {location.buttonClicks.toLocaleString(getInterfaceLanguage())}
                    </TableCell>
                  )}
                  {columns.find(c => c.id === 'engagement')?.visible && (
                    <TableCell className="text-right font-semibold text-[#1C2434] text-[12px]" style={{ padding: '8px 12px' }}>
                      {location.engagement}%
                    </TableCell>
                  )}
                  {columns.find(c => c.id === 'pitchRetention')?.visible && (
                    <TableCell className="text-right font-semibold text-[#1C2434] text-[12px]" style={{ padding: '8px 12px' }}>
                      {location.pitchRetention != null ? `${location.pitchRetention.toFixed(1)}%` : '—'}
                    </TableCell>
                  )}
                  {columns.find(c => c.id === 'conversions')?.visible && (
                    <TableCell className="text-right font-semibold text-[#1C2434] text-[12px]" style={{ padding: '8px 12px' }}>
                      {location.conversions.toLocaleString(getInterfaceLanguage())}
                    </TableCell>
                  )}
                  {columns.find(c => c.id === 'revenue')?.visible && (
                    <TableCell className="text-right text-[12px]" style={{ padding: '8px 12px' }}>
                      <span className="text-[12px] text-[#1C2434]">R$</span>
                      <span className="text-[13px] font-bold text-[#1C2434] ml-0.5">
                        {location.revenue.toLocaleString(getInterfaceLanguage(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
